import { Card } from '../../../../components/atoms';
import { useTranslation } from 'react-i18next';
import { HiCheck } from 'react-icons/hi2';
import { useCallback, useState } from 'react';
import { errorToast, successToast } from '../../../../utils/toasts';
import { useAppDispatch, useAppSelector } from '../../../../hooks/hooksRedux';
import { addPersonalitySlug, markLanguagePersisted } from '../../../../store/globalSlice/languageSlice/LanguageSlice';
import { useAuth } from '../../../../store/auth';
import { RootState } from '../../../../store';
import { AccountService } from '../../../../services/AccountService';
import { errorMessageAdapter } from '../../../../adapters';
import { useApi } from '../../../../services/hooks/apiHook';
import { AxiosInstance } from 'axios';
import { useLocation } from 'react-router-dom';

const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'pt-BR', name: 'Português', flag: '🇧🇷' },
];

const LanguageCard = () => {
  const { i18n, t } = useTranslation();
  const { token } = useAuth();
  const location = useLocation();
  const api: AxiosInstance = useApi();
  const dispatch = useAppDispatch();
  const { legalPersonalitySlug } = useAppSelector((state: RootState) => state.language);
  
  const [selectedLanguage, setSelectedLanguage] = useState(legalPersonalitySlug || i18n.language);
  const [isUpdating, setIsUpdating] = useState(false);

  const fetchLanguagePersistence = useCallback(
    async (selectedLang: string) => {
      if (!token) {
        return;
      }

      try {
        setIsUpdating(true);
        await AccountService.updatedLanguageState(
          api,
          { preferred_language: selectedLang },
          {
            headers: { 'Content-Language': selectedLang, Authorization: `Bearer ${token}` }
          }
        );
        dispatch(markLanguagePersisted({ language: selectedLang, at: Date.now() }));
        successToast(t('preferences.languageUpdated'));
      } catch (error) {
        console.error(error);
        errorToast(errorMessageAdapter(error));
      } finally {
        setIsUpdating(false);
      }
    },
    [token, api, t, dispatch]
  );

  const handleLanguageChange = (languageCode: string) => {
    if (languageCode === selectedLanguage) {
      return;
    }

    dispatch(addPersonalitySlug(languageCode));
    setSelectedLanguage(languageCode);
    void i18n.changeLanguage(languageCode);
    void fetchLanguagePersistence(languageCode);
  };

  return (
    <Card.Root className="w-full">
      <Card.Header>
        <div className="flex flex-col gap-1">
          <h3 className="text-lg font-semibold">{t('preferences.languagePreferences')}</h3>
          <p className="text-sm text-gray-500">{t('preferences.chooseLanguage')}</p>
        </div>
      </Card.Header>

      <Card.Content>
        <div className="flex flex-col gap-3">
          {languages.map((language) => (
            <button
              key={language.code}
              onClick={() => handleLanguageChange(language.code)}
              disabled={isUpdating}
              className={`flex items-center justify-between gap-4 rounded-lg border-2 p-4 transition-all hover:border-blue-300 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed ${
                selectedLanguage === language.code
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-center gap-3 flex-1">
                <span className="text-2xl">{language.flag}</span>
                <div className="flex flex-col items-start">
                  <span className="text-sm font-medium text-gray-800">{language.name}</span>
                  <span className="text-xs text-gray-500">{language.code}</span>
                </div>
              </div>
              {selectedLanguage === language.code && (
                <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-500">
                  <HiCheck className="text-white" size={16} />
                </div>
              )}
            </button>
          ))}
        </div>
      </Card.Content>
    </Card.Root>
  );
};

export { LanguageCard };

